/**
 * Lightweight client-side A/B testing.
 *
 * Each visitor gets assigned a stable variant (50/50 by default) on first
 * visit, persisted in localStorage so they stay in the same bucket across
 * sessions. A `variant_assigned` GA4 event fires once per (test, variant)
 * per visitor so you can attribute downstream events.
 *
 * No server-side splitting, no flicker — variant is read synchronously.
 *
 * Usage:
 *   const variant = useABVariant('hero_urgency_copy', ['v_2k', 'v_1235']);
 *   <p>{variant === 'v_2k' ? '~$2,000' : '~$1,235'}</p>
 */
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from './analytics';

const STORAGE_KEY = 'ascend_ab_v1';

function readBucket() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function writeBucket(b) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(b)); } catch (_e) { /* noop */ }
}

// Stable hash of (visitorId + testName) so a given visitor always lands in
// the same bucket regardless of ordering. Visitor ID is a random 12-char id
// generated once and persisted.
function getOrCreateVisitorId() {
  try {
    const k = 'ascend_visitor_id';
    let v = localStorage.getItem(k);
    if (!v) {
      v = Math.random().toString(36).slice(2, 14);
      localStorage.setItem(k, v);
    }
    return v;
  } catch {
    return 'anon';
  }
}

export { getOrCreateVisitorId };

function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
  return Math.abs(h);
}

export function useABVariant(testName, variants) {
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const bucket = readBucket();
    if (bucket[testName] && variants.includes(bucket[testName])) {
      initialRef.current = bucket[testName];
    } else {
      const visitor = getOrCreateVisitorId();
      const idx = hashStr(visitor + ':' + testName) % variants.length;
      initialRef.current = variants[idx];
      bucket[testName] = initialRef.current;
      writeBucket(bucket);
    }
  }
  const [variant] = useState(initialRef.current);

  // Fire impression event ONCE per (test, variant) per visitor so GA4 can
  // do downstream attribution without inflated counts. Also POSTs a
  // server-side `cta_ab_impression` event when this is the CTA-copy test,
  // so the admin funnel can compute conversion even when ad-blockers gate
  // GA4. Best-effort: never throw.
  useEffect(() => {
    try {
      const seenKey = `ascend_ab_seen_${testName}_${variant}`;
      if (!localStorage.getItem(seenKey)) {
        localStorage.setItem(seenKey, '1');
        trackEvent('ab_variant_assigned', { test: testName, variant });
        // Server-side impression for CTA copy test (uses a generic event_type
        // with metadata so we don't need a new event per test).
        if (testName === 'cta_copy_free_ats_bottom') {
          const { trackServerEvent } = require('./trackServerEvent');
          trackServerEvent('cta_ab_impression', { test: testName, variant });
        }
      }
    } catch (_e) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return variant;
}
